// Canonical model. scripts/sync-licensing.js mirrors this file into src/lib/licensing/.
const registry = require('./licensePolicies.json');
const AUDIO_TYPES = new Set(['singlesound', 'singlesounds', 'oneshot', 'oneshots', 'singlesfx',
  'soundpack', 'soundpacks', 'samplepack', 'samplepacks']);
const unknownPermissions = () => ({ commercialUse: null, modification: null, agentAssistance: null,
  standaloneRedistribution: null, modelTraining: null });

function isPolicyApproved(policy) {
  return policy?.status === 'approved' && Boolean(policy.licensor && policy.sections?.length &&
    policy.termsDigest && policy.approval?.termsDigest === policy.termsDigest &&
    Number.isFinite(Date.parse(policy.approval?.approvedAt)));
}
function publicTermsUrl(value, origin = registry.origin, data = registry) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, origin);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password ||
      /\/(downloads|private)\//i.test(decodeURIComponent(url.pathname)) ||
      /[?&](token|signature|x-goog-[^=]*|googleaccessid)=/i.test(url.search)) return null;
    // The review page contains drafts, so it must never become an applicable license URL.
    const pathname = decodeURIComponent(url.pathname).replace(/\/+$/, '').toLowerCase();
    if (url.origin === new URL(origin).origin && (pathname === '/licensing' || pathname.startsWith('/licensing/'))) {
      const ownedPolicy = data.policies.find((policy) => policy.termsUrl &&
        new URL(policy.termsUrl).pathname.toLowerCase() === pathname);
      if (!ownedPolicy || !isPolicyApproved(ownedPolicy)) return null;
    }
    return url.href;
  } catch { return null; }
}
function isAudioProduct(product) {
  const type = String(product?.productType || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return ['native', 'free'].includes(product?.listingType) && AUDIO_TYPES.has(type);
}
function matchesPolicy(product, policy) {
  return isAudioProduct(product) &&
    (policy.kind === 'free-audio' ? product.isFree === true : policy.kind === 'paid-audio' && product.isFree === false);
}
function attributionCredit(product, data) {
  // Only this explicitly public credit is used; never infer a name from createdBy or a private profile.
  const value = product?.attributionAuthor;
  const author = typeof value === 'string' && value.trim() && value.trim().length <= 160 &&
    Array.from(value).every((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127) ?
    value.trim() : null;
  return { attributionAuthor: author,
    attributionText: author ? data.attribution.text.replace('[Author]', () => author) : null,
    attributionUrl: data.attribution.url };
}
function policyProposal(policy, data, product) {
  return {
    id: policy.id, version: policy.version, status: policy.status, title: policy.title,
    reviewUrl: policy.termsUrl || data.origin + policy.reviewPath,
    attributionRequired: policy.attributionRequired === true,
    ...attributionCredit(product, data),
  };
}
/** Effective permissions stay unknown for drafts, unassigned records and external references. */
function getProductLicensing(product, data = registry) {
  const base = { status: 'unassigned', id: null, version: null, url: null,
    licensor: null, termsDigest: null, summary: null,
    attributionRequired: null, attributionAuthor: null, attributionText: null, attributionUrl: null,
    permissions: unknownPermissions(), proposal: null };
  const external = ['legacyExternal', 'externalMarketplace', 'linkOnly'].includes(product?.listingType);
  if (external) return { ...base, status: 'external', url: publicTermsUrl(product.licenseUrl, data.origin, data) };
  const selected = data.policies.find((policy) => policy.id === product?.licenseId);
  if (product?.licenseId && (!selected || !matchesPolicy(product, selected))) {
    return { ...base, status: 'needs-review' };
  }
  if (selected?.status === 'approved') {
    const url = publicTermsUrl(selected.termsUrl, data.origin, data);
    const credit = attributionCredit(product, data);
    if (!isPolicyApproved(selected) || product.originalAudioRightsConfirmed !== true ||
      !url || !selected.version || !selected.permissions ||
      (selected.attributionRequired && !credit.attributionAuthor)) return { ...base, status: 'needs-review' };
    return { ...base, status: 'approved', id: selected.id, version: selected.version, url,
      licensor: selected.licensor, termsDigest: selected.termsDigest, summary: selected.summary || null,
      attributionRequired: selected.attributionRequired === true,
      ...credit,
      permissions: { ...unknownPermissions(), ...Object.fromEntries(Object.keys(unknownPermissions())
        .map((key) => [key, typeof selected.permissions[key] === 'boolean' ? selected.permissions[key] : null])) } };
  }
  if (selected) return { ...base, status: selected.status === 'draft' ? 'draft' : 'pending-approval',
    url: publicTermsUrl(product?.licenseUrl, data.origin, data),
    proposal: policyProposal(selected, data, product) };
  const legacyUrl = publicTermsUrl(product?.licenseUrl, data.origin, data);
  if (legacyUrl) return { ...base, status: 'terms-provided', url: legacyUrl };
  // No assignment means no applicable policy or implicit historical draft.
  return base;
}
function validateProductLicenseSelection(product) {
  if (!product?.licenseId) return;
  const selected = registry.policies.find((policy) => policy.id === product.licenseId);
  if (!selected || !matchesPolicy(product, selected)) {
    throw new Error('Choose a license policy matching this audio product and its free/paid status, ' +
      'or leave it unassigned.');
  }
  if (selected.status === 'approved' && getProductLicensing(product).status !== 'approved') {
    throw new Error('Approved audio requires confirmed recording rights and complete public credit.');
  }
}
module.exports = { registry, isPolicyApproved, publicTermsUrl, isAudioProduct,
  getProductLicensing, validateProductLicenseSelection, attributionCredit };
